import {
    CButton,
    CCard,
    CCardBody,
    CCardGroup,
    CCol,
    CContainer,
    CForm,
    CRow,
    CFormSelect,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { t } from "i18next";
import { useState } from "react";
import {
    removeError,
    requiredField,
    validateEmail,
    validatePasswordMatch,
    validatePasswordStrength,
    validatePhone,
    validateDate
} from "@webkit/functions/Validators";
import Input from "@webkit/components/Input.js";
import {
    cilLockLocked,
    cilUser,
    cilEnvelopeOpen,
    cilLowVision,
    cilPhone,
    cilCalendar,
} from "@coreui/icons";
import AuthService from "@services/auth.service";
import { useNavigate } from "react-router-dom";
import { Person } from "@models/person.model";
import { useSpinner } from '@webkit/components/typescript/SpinnerContext';
import { getToastStore } from '@utils/ToastStore';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import AppEnviroment from "app-enviroment";

const SignUpForm = () => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const ctx = getToastStore();
    const { showSpinner, hideSpinner } = useSpinner();
    const [blockButton, setBlockButton] = useState(true)
    const buttonStyle = {
        backgroundColor: '#00BFA6',
        color: 'white',
        borderRadius: '25px',
        marginTop: '10px',
    };
    const [formData, setFormData] = useState({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
        phone: "",
        birthDate: "",
        gender: "",
    });
    const [formErrors, setFormErrors] = useState({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
        phone: "",
        birthDate: "",
        gender: "",
        terms: ""
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [acceptTerms, setAcceptTerms] = useState(false);

    const navigate = useNavigate();

    const togglePassword = () => setShowPassword((prev) => !prev);
    const toggleConfirmPassword = () => setShowConfirmPassword((prev) => !prev);

    const validateForm = (val) => {
        const name = val?.target?.name;
        let errors = { ...formErrors };
        let isValid = true;

        const requiredFields = [
            "username",
            "email",
            "password",
            "confirmPassword",
            "firstName",
            "lastName",
            "terms"
        ];

        const validateField = (field) => {
            if (requiredFields.includes(field)) {
                isValid = requiredField(formData, errors, field, isValid);
                removeError(isValid, errors, field);
            }

            if (field === "email" && formData.email) {
                isValid = validateEmail(formData.email, errors) && isValid;
            }

            if (field === "password" && formData.password) {
                isValid = validatePasswordStrength(formData.password, errors) && isValid;
            }

            if (field === "phone" && formData.phone) {
                isValid = validatePhone(formData.phone, errors, 'phone') && isValid;
            }

            if (field === "birthDate" && formData.birthDate) {
                isValid = validateDate(formData.birthDate, errors, field) && isValid;
            }

            if ((field === "password" || field === "confirmPassword") &&
                formData.password &&
                formData.confirmPassword
            ) {
                isValid = validatePasswordMatch(formData.password, formData.confirmPassword, errors) && isValid;
            }
        };

        if (name) {
            validateField(name);
        } else {
            Object.keys(formData).forEach((field) => validateField(field));
        }

        setFormErrors(errors);
        const allFieldsFilled = formData.username && formData.email && formData.password && formData.confirmPassword && formData.firstName && formData.lastName && acceptTerms;
        setBlockButton(!allFieldsFilled);
        return isValid;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateForm()) return

        try {
            showSpinner()
            // Ottieni il token
            const token = await executeRecaptcha('register');
            try {
                const response = await AuthService.captchaCheck(token);
                if (response) {
                    const person = new Person(
                        formData.username.trim(),
                        formData.firstName.trim(),
                        formData.lastName.trim(),
                        formData.email.trim(),
                        formData.phone.trim(),
                        "",
                        (navigator.language || navigator.userLanguage).split('-')[0],
                        "",
                        formData.birthDate.trim(),
                        formData.gender.trim(),
                    )

                    const response = await AuthService.handleSignUp(person, formData.username.trim(), formData.password.trim());
                    const msg = response?.data?.message || t("success.registration_success")
                    ctx.setToastMsg(msg);
                    ctx.setSeeToast(true);
                    ctx.setErrorToast(false)
                    navigate("/login");
                }
            } catch (e) {
                console.error("Errore nella registrazione:", e.message);
            }
        } catch (error) {
            console.error("Errore nella registrazione:", error);
        } finally {
            hideSpinner()
        }
    };

    return (
        <div className="min-vh-100 d-flex flex-row align-items-center smart-login-container">
            <CContainer>
                <CRow className="justify-content-center">
                    <CCol md={6}>
                        <CCardGroup>
                            <CCard className="p-4 text-center smart-login-container-card">
                                <CCardBody>
                                    <CForm onSubmit={handleSubmit}>
                                        <h1>{t("label.signup")}</h1>
                                        <p className="text-body-secondary">
                                            {t("label.create_account")}
                                        </p>

                                        <Input
                                            className="col-12"
                                            name="firstName"
                                            label="label.first_name"
                                            required
                                            formData={formData}
                                            formErrors={formErrors}
                                            validateForm={validateForm}
                                            handleChange={handleChange}
                                        />
                                        <Input
                                            className="col-12"
                                            name="lastName"
                                            label="label.last_name"
                                            required
                                            formData={formData}
                                            formErrors={formErrors}
                                            validateForm={validateForm}
                                            handleChange={handleChange}
                                        />
                                        <Input
                                            className="col-12"
                                            name="username"
                                            label="label.username"
                                            required
                                            icon={cilUser}
                                            formData={formData}
                                            formErrors={formErrors}
                                            validateForm={validateForm}
                                            handleChange={handleChange}
                                        />
                                        <Input
                                            className="col-12"
                                            name="email"
                                            label="label.email"
                                            required
                                            icon={cilEnvelopeOpen}
                                            formData={formData}
                                            formErrors={formErrors}
                                            validateForm={validateForm}
                                            handleChange={handleChange}
                                        />
                                        <Input
                                            className="col-12"
                                            name="phone"
                                            label="label.phone_number"
                                            type='tel'
                                            icon={cilPhone}
                                            placeholder="Inserisci il numero di telefono"
                                            formData={formData}
                                            validateForm={validateForm}
                                            handleChange={handleChange}
                                        />
                                        <Input
                                            className="col-12"
                                            name="birthDate"
                                            label="label.birth_date"
                                            type="date"
                                            icon={cilCalendar}
                                            formData={formData}
                                            validateForm={validateForm}
                                            handleChange={handleChange}
                                        />

                                        <CFormSelect
                                            className="mb-3"
                                            name="gender"
                                            aria-label={t("label.select_gender")}
                                            value={formData.gender}
                                            onChange={handleChange}
                                            onBlur={validateForm}
                                        >
                                            <option value="">{t("label.select_gender")}</option>
                                            <option value="male">{t("label.male")}</option>
                                            <option value="female">{t("label.female")}</option>
                                            <option value="other">{t("label.other")}</option>
                                        </CFormSelect>

                                        <div className="position-relative">
                                            <Input
                                                className="col-12"
                                                name="password"
                                                label="label.password"
                                                required
                                                type={showPassword ? "text" : "password"}
                                                icon={cilLockLocked}
                                                formData={formData}
                                                formErrors={formErrors}
                                                validateForm={validateForm}
                                                handleChange={handleChange}
                                            />
                                            <span
                                                style={{ position: "absolute", top: "40px", right: "10px", cursor: "pointer" }}
                                                onClick={togglePassword}
                                                title={showPassword ? "Hide password" : "Show password"}
                                            >
                                                <CIcon icon={cilLowVision} />
                                            </span>
                                        </div>

                                        <div className="position-relative">
                                            <Input
                                                className="col-12"
                                                name="confirmPassword"
                                                label="label.confirm_password"
                                                required
                                                type={showConfirmPassword ? "text" : "password"}
                                                icon={cilLockLocked}
                                                formData={formData}
                                                formErrors={formErrors}
                                                validateForm={validateForm}
                                                handleChange={handleChange}
                                            />
                                            <span
                                                style={{ position: "absolute", top: "40px", right: "10px", cursor: "pointer" }}
                                                onClick={toggleConfirmPassword}
                                                title={showConfirmPassword ? "Hide password" : "Show password"}
                                            >
                                                <CIcon icon={cilLowVision} />
                                            </span>
                                        </div>

                                        <div style={{ marginBottom: 20 }}>
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                <input
                                                    type="checkbox"
                                                    name="terms"
                                                    checked={acceptTerms}
                                                    onChange={() => {
                                                        const newValue = !acceptTerms;
                                                        setAcceptTerms(newValue);
                                                        if (newValue) {
                                                            setFormErrors(prev => ({ ...prev, terms: '' }));
                                                        }
                                                        const allFieldsFilled = formData.username && formData.email && formData.password && formData.confirmPassword && formData.firstName && formData.lastName && newValue;
                                                        setBlockButton(!allFieldsFilled);
                                                    }}
                                                    style={{
                                                        width: 15,
                                                        height: 15,
                                                        accentColor: "var(--primary)",
                                                        cursor: "pointer",
                                                        marginRight: "10px"
                                                    }}
                                                />
                                                <span>
                                                    {t("signup.accept")}{" "}
                                                    <a href="https://hostyble.netlify.app/terms-conditions/" target="_blank" rel="noopener noreferrer">
                                                        {t("signup.terms")}
                                                    </a>
                                                    {" "}{t("signup.and")}{" "}
                                                    <a href="https://hostyble.netlify.app/privacy-policy/" target="_blank" rel="noopener noreferrer">
                                                        {t("signup.privacy")}
                                                    </a>
                                                </span>
                                            </div>
                                            {formErrors.terms && (
                                                <span className="text-danger">{formErrors.terms}</span>
                                            )}
                                        </div>

                                        <CRow>
                                            <CCol xs={12} className="d-flex justify-content-center">
                                                <CButton
                                                    style={buttonStyle}
                                                    type='submit'
                                                    className='px-4 col-12 text-uppercase'
                                                    disabled={blockButton}
                                                >
                                                    {t("label.signup")}
                                                </CButton>
                                            </CCol>
                                            <p className='text-body-secondary mt-3'>
                                                {t('label.alredy_registered')}{" "}
                                                <a href="/login">{t('label.click_here')}</a>
                                            </p>
                                        </CRow>
                                    </CForm>
                                </CCardBody>
                            </CCard>
                        </CCardGroup>
                    </CCol>
                </CRow>
            </CContainer>
        </div>
    );
};
const SignUp = () => {
    const siteKey= AppEnviroment.getRecaptchaSite();
    return (
      <GoogleReCaptchaProvider reCaptchaKey={siteKey}>
        <SignUpForm />
      </GoogleReCaptchaProvider>
    );
};

export default SignUp;